import * as React from 'react';

import type { ChatSocketLike } from '@/api/chat-realtime';
import type { ThreadFilters } from '@/api/chat-client';
import type { KbFilters } from '@/api/kb-client';
import { useThreadsQuery } from '@/api/use-chat';
import { useKbThreadsQuery } from '@/api/use-kb';
import { useSupplierDashboard } from '@/api/use-supplier-dashboard';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ChatThreadPanel } from '@/routes/communication/chat-thread-panel';
import { KbThreadDetail } from '@/routes/communication/kb-thread-detail';
import {
  applyFilters,
  buildCampaignNameMap,
  campaignOptions,
  mergeConversations,
  type EchangesFilters,
  type UnifiedConversation,
} from '@/routes/supplier/communication/echanges-model';
import { cn } from '@/lib/utils';

// =============================================================================
// supplier-echanges-screen.tsx — FOURNISSEUR « Mes échanges avec JeKo » (redesign GATE 2026-09-12).
// =============================================================================
// UNE page unique pour la consultation des communications fournisseur↔JeKo : filtres posés à plat (pas
// de carte), grille liste (gauche, 340px) + détail (droite), largeur portée à 1400px, panneaux d'au
// moins 480px alignés (items-stretch). La liste FUSIONNE côté client deux read-models (chat EN COURS +
// KB ARCHIVÉES) en un modèle unifié trié par récence (echanges-model, sélecteurs purs). Le détail est
// INTERACTIF pour un fil en cours (ChatThreadPanel : composer + markRead + temps réel) et en LECTURE
// SEULE pour un fil archivé (KbThreadDetail). L'écran porte SON PROPRE en-tête de panneau unifié
// (ChatThreadPanel/KbThreadDetail montés avec `hideHeader` — additif, rétro-compatible).
//
// Composant PRÉSENTATIONNEL : filtres d'URL + fil sélectionné portés en props (RULE_REF_006) → testable
// hors routeur. La recherche texte de liste + l'ouverture/fermeture du popover période sont de simples
// états de VUE éphémères (in-memory React, aucun localStorage). WCAG 2.1 AA : régions titrées, <label>
// liés, chips avec aria-pressed, aucune info portée par la seule couleur (badges + libellés textuels).

/** Filtres serveur STABLES (aucun filtre) : la fusion + le filtrage se font entièrement côté client. */
const EMPTY_THREAD_FILTERS: ThreadFilters = {};
const EMPTY_KB_FILTERS: KbFilters = {};

/** « 10 sept. » — jour + mois courts SANS heure. Accepte une date ISO complète ou une date YYYY-MM-DD. */
function formatShortDay(iso: string): string {
  const date = new Date(iso.length === 10 ? `${iso}T12:00:00` : iso);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
}

/** « 10 sept. à 09:00 » — jour court + heure (en-tête du panneau, « Ouverte le … »). */
function formatOpenedAt(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  const time = date.toLocaleTimeString('fr-FR', {
    hour: '2-digit',
    minute: '2-digit',
  });
  return `${formatShortDay(iso)} à ${time}`;
}

export interface SupplierEchangesScreenProps {
  readonly filters: EchangesFilters;
  readonly onFiltersChange: (filters: EchangesFilters) => void;
  readonly selectedThreadId: string | null;
  readonly onSelectThread: (threadId: string | null) => void;
  /** Fabrique de socket injectable (tests) — transmise au panneau chat (temps réel). */
  readonly socketFactory?: () => ChatSocketLike;
}

export function SupplierEchangesScreen({
  filters,
  onFiltersChange,
  selectedThreadId,
  onSelectThread,
  socketFactory,
}: SupplierEchangesScreenProps): React.JSX.Element {
  // La fusion est CÔTÉ CLIENT : on charge les DEUX listes complètes avec une clé de query STABLE (`{}`),
  // puis TOUS les filtres (campagne / période / archivées / non-lues) s'appliquent en mémoire. Ne PAS
  // transmettre le filtre campagne au serveur : cela changerait la clé de query et provoquerait un flash
  // « chargement » à chaque changement de filtre (le read-model est de toute façon re-cloisonné RLS côté API).
  const chatThreadsQuery = useThreadsQuery('supplier', EMPTY_THREAD_FILTERS);
  const kbThreadsQuery = useKbThreadsQuery('supplier', EMPTY_KB_FILTERS);
  const dashboard = useSupplierDashboard();

  const nameByCampaign = React.useMemo(
    () => buildCampaignNameMap(dashboard.data),
    [dashboard.data],
  );
  const options = React.useMemo(
    () => campaignOptions(dashboard.data),
    [dashboard.data],
  );

  // Fusion COMPLÈTE (non filtrée) : sert à résoudre la SOURCE du fil sélectionné (chat vs kb) même quand
  // les filtres l'excluent de la liste visible.
  const merged = React.useMemo(
    () =>
      mergeConversations(
        chatThreadsQuery.data?.threads ?? [],
        kbThreadsQuery.data?.threads ?? [],
        nameByCampaign,
      ),
    [chatThreadsQuery.data, kbThreadsQuery.data, nameByCampaign],
  );

  // Recherche texte de liste (point 7) — état de VUE éphémère, filtre client-side sur titre + preview.
  const [listQuery, setListQuery] = React.useState('');

  const visible = React.useMemo(
    () => applyFilters(merged, filters),
    [merged, filters],
  );

  const displayed = React.useMemo(() => {
    const needle = listQuery.trim().toLocaleLowerCase('fr');
    if (needle.length === 0) {
      return visible;
    }
    return visible.filter((c) => {
      const title = (c.campaignName ?? `Campagne ${c.campaignId}`).toLocaleLowerCase('fr');
      const preview = (c.preview ?? '').toLocaleLowerCase('fr');
      return title.includes(needle) || preview.includes(needle);
    });
  }, [visible, listQuery]);

  // Sélection PAR DÉFAUT (point 12) : sans `thread` en URL, on ouvre la 1re conversation de la liste
  // affichée (dérivation PURE, sans navigation — aucun bruit d'URL tant que l'utilisateur n'a pas cliqué).
  const effectiveSelectedId =
    selectedThreadId ?? displayed[0]?.threadId ?? null;

  const selectedConversation =
    effectiveSelectedId === null
      ? undefined
      : merged.find((c) => c.threadId === effectiveSelectedId);

  const isLoading = chatThreadsQuery.isLoading || kbThreadsQuery.isLoading;

  // Les filtres s'appliquent AU CHANGEMENT (point 6, plus de bouton « Rechercher »). `emit` construit un
  // EchangesFilters PROPRE : `undefined` dans l'override = inchangé ; `null`/'' = retire la clé (URL propre,
  // exactOptionalPropertyTypes : on OMET la clé plutôt que de la porter à `undefined`).
  const emit = (override: {
    includeArchived?: boolean;
    unreadOnly?: boolean;
    campaignId?: string | null;
    dateFrom?: string | null;
    dateTo?: string | null;
  }): void => {
    const pick = (
      next: string | null | undefined,
      current: string | undefined,
    ): string | undefined => {
      if (next === undefined) {
        return current;
      }
      return next === null || next.length === 0 ? undefined : next;
    };
    const campaignId = pick(override.campaignId, filters.campaignId);
    const dateFrom = pick(override.dateFrom, filters.dateFrom);
    const dateTo = pick(override.dateTo, filters.dateTo);
    onFiltersChange({
      includeArchived: override.includeArchived ?? filters.includeArchived,
      unreadOnly: override.unreadOnly ?? filters.unreadOnly,
      ...(campaignId !== undefined ? { campaignId } : {}),
      ...(dateFrom !== undefined ? { dateFrom } : {}),
      ...(dateTo !== undefined ? { dateTo } : {}),
    });
  };

  const onCampaignChange = (value: string): void =>
    emit({ campaignId: value.length > 0 ? value : null });
  const onPeriodChange = (from: string, to: string): void =>
    emit({ dateFrom: from.length > 0 ? from : null, dateTo: to.length > 0 ? to : null });

  const count = displayed.length;

  return (
    <div className="mx-auto flex w-full max-w-[1400px] flex-col gap-6">
      <header className="flex flex-col gap-1">
        <h1 className="font-serif text-2xl font-semibold tracking-tight">
          Mes échanges avec JeKo
        </h1>
        <p className="text-sm text-muted-foreground">
          Vos conversations avec l'équipe JeKo, en cours et archivées.
        </p>
      </header>

      {/* Filtres posés À PLAT (pas de carte), sur une seule ligne (points 2-6). */}
      <div
        role="search"
        aria-label="Filtrer mes conversations"
        className="flex flex-wrap items-end gap-4"
      >
        <div className="flex flex-col gap-1.5">
          <Label
            htmlFor="echanges-campaign"
            className="font-mono text-[11px] font-medium uppercase tracking-wider text-muted-foreground"
          >
            Campagne
          </Label>
          <select
            id="echanges-campaign"
            name="campaign"
            value={filters.campaignId ?? ''}
            onChange={(event) => onCampaignChange(event.target.value)}
            className="h-9 w-64 rounded-md border border-input bg-background px-3 text-sm text-foreground shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          >
            <option value="">Toutes les campagnes</option>
            {options.map((option) => (
              <option key={option.campaignId} value={option.campaignId}>
                {option.intitule}
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-col gap-1.5">
          <span
            id="echanges-period-label"
            className="font-mono text-[11px] font-medium uppercase tracking-wider text-muted-foreground"
          >
            Période
          </span>
          <PeriodRangeFilter
            labelledBy="echanges-period-label"
            from={filters.dateFrom ?? ''}
            to={filters.dateTo ?? ''}
            onChange={onPeriodChange}
          />
        </div>

        {/* Chips toggle (point 5) — aria-pressed, état visuel actif/inactif, libellé textuel. */}
        <div className="ml-auto flex items-center gap-2 self-end">
          <FilterChip
            pressed={filters.unreadOnly}
            onToggle={() => emit({ unreadOnly: !filters.unreadOnly })}
          >
            Non lues
          </FilterChip>
          <FilterChip
            pressed={filters.includeArchived}
            onToggle={() => emit({ includeArchived: !filters.includeArchived })}
          >
            Archivées
          </FilterChip>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[340px_1fr] lg:items-stretch">
        {/* Colonne gauche : recherche + compteur + liste (point 16 : min-h 480px). */}
        <Card className="flex min-h-[480px] flex-col overflow-hidden">
          <div className="flex flex-col gap-2 border-b border-border p-3">
            <Input
              type="search"
              aria-label="Rechercher une conversation"
              placeholder="Rechercher une conversation…"
              value={listQuery}
              onChange={(event) => setListQuery(event.target.value)}
              className="h-9"
            />
            <p className="font-mono text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
              {count} {count > 1 ? 'CONVERSATIONS' : 'CONVERSATION'}
            </p>
          </div>
          <div
            aria-label="Liste de mes conversations avec JeKo"
            className="min-h-0 flex-1 overflow-y-auto"
          >
            <EchangesConversationList
              conversations={displayed}
              selectedThreadId={effectiveSelectedId}
              onSelectThread={onSelectThread}
              isLoading={isLoading}
              archivedHidden={!filters.includeArchived}
            />
          </div>
        </Card>

        {/* Colonne droite : en-tête de panneau UNIFIÉ + corps (interactif ou lecture seule). */}
        <Card className="flex min-h-[480px] flex-col overflow-hidden">
          {selectedConversation === undefined ? (
            <p className="p-6 text-sm text-muted-foreground">
              Aucune conversation à afficher.
            </p>
          ) : (
            <>
              <EchangesDetailHeader conversation={selectedConversation} />
              <div className="min-h-0 flex-1">
                {selectedConversation.source === 'chat' ? (
                  // EN COURS → INTERACTIF (messages + composition + markRead + temps réel).
                  <ChatThreadPanel
                    profile="supplier"
                    threadId={selectedConversation.threadId}
                    hideHeader
                    composerPlaceholder="Votre message à l’équipe JeKo…"
                    emptyStateText="Aucun message dans cette conversation. Écrivez le premier ci-dessous."
                    {...(socketFactory !== undefined ? { socketFactory } : {})}
                  />
                ) : (
                  // ARCHIVÉE → LECTURE SEULE (pas de composer).
                  <KbThreadDetail
                    profile="supplier"
                    threadId={selectedConversation.threadId}
                    hideHeader
                  />
                )}
              </div>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// En-tête UNIFIÉ du panneau de détail (point 13).
// -----------------------------------------------------------------------------

function EchangesDetailHeader({
  conversation,
}: {
  readonly conversation: UnifiedConversation;
}): React.JSX.Element {
  const title = conversation.campaignName ?? `Campagne ${conversation.campaignId}`;
  const opened =
    conversation.date !== null ? formatOpenedAt(conversation.date) : null;
  return (
    // <div> et non <header> : à l'intérieur d'une Card (div non sectionnante), un <header> deviendrait un
    // 2e landmark « banner » (axe landmark-no-duplicate-banner). Le titre reste porté par le <h2>.
    <div className="flex items-start justify-between gap-4 border-b border-border px-6 py-4">
      <div className="min-w-0">
        <h2 className="truncate font-serif text-base font-semibold">{title}</h2>
        <p className="text-xs text-muted-foreground">
          {opened !== null ? `Ouverte le ${opened} · ` : ''}avec l'équipe JeKo
        </p>
      </div>
      {/* GATE_HUMAN 2026-09-12 : l'action « Archiver » est RETIRÉE de l'UI (hors spécifications ; l'archivage
          est déclenché par l'admin/le système). On conserve UNIQUEMENT le rappel textuel de l'état pour un
          fil déjà archivé (info, jamais une couleur seule), sans aucune action. */}
      {conversation.isArchived ? <Badge variant="outline">Archivée</Badge> : null}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Chip de filtre toggle (point 5) — bouton aria-pressed, état visuel actif/inactif.
// -----------------------------------------------------------------------------

function FilterChip({
  pressed,
  onToggle,
  children,
}: {
  readonly pressed: boolean;
  readonly onToggle: () => void;
  readonly children: React.ReactNode;
}): React.JSX.Element {
  return (
    <button
      type="button"
      aria-pressed={pressed}
      onClick={onToggle}
      className={cn(
        'inline-flex h-9 items-center rounded-full border px-3 text-sm font-medium transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        pressed
          ? 'border-primary bg-primary text-primary-foreground'
          : 'border-input bg-background text-muted-foreground hover:bg-accent hover:text-accent-foreground',
      )}
    >
      {children}
    </button>
  );
}

// -----------------------------------------------------------------------------
// Contrôle de plage de PÉRIODE (point 4) — trigger stylé (aucun placeholder natif dd/mm/yyyy visible par
// défaut) ouvrant un popover à deux champs date « Du » / « Au ». Filtre client-side sur `date`.
// -----------------------------------------------------------------------------

function periodTriggerLabel(from: string, to: string): string {
  if (from.length > 0 && to.length > 0) {
    return `${formatShortDay(from)} – ${formatShortDay(to)}`;
  }
  if (from.length > 0) {
    return `Depuis le ${formatShortDay(from)}`;
  }
  if (to.length > 0) {
    return `Jusqu'au ${formatShortDay(to)}`;
  }
  return 'Toutes les périodes';
}

function PeriodRangeFilter({
  from,
  to,
  onChange,
  labelledBy,
}: {
  readonly from: string;
  readonly to: string;
  readonly onChange: (from: string, to: string) => void;
  readonly labelledBy: string;
}): React.JSX.Element {
  const [open, setOpen] = React.useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const hasRange = from.length > 0 || to.length > 0;

  // Fermeture au clic à l'extérieur + à la touche Échap (a11y clavier).
  React.useEffect(() => {
    if (!open) {
      return;
    }
    const onPointerDown = (event: MouseEvent): void => {
      if (
        containerRef.current !== null &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        // Nom accessible = libellé du champ (« Période ») + valeur courante (état lu au lecteur d'écran).
        aria-labelledby={`${labelledBy} echanges-period-value`}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
        className={cn(
          'flex h-9 w-56 items-center justify-between rounded-md border border-input bg-background px-3 text-sm shadow-sm',
          'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
          hasRange ? 'text-foreground' : 'text-muted-foreground',
        )}
      >
        <span id="echanges-period-value" className="truncate">
          {periodTriggerLabel(from, to)}
        </span>
        <span aria-hidden="true" className="ml-2 text-muted-foreground">
          ▾
        </span>
      </button>

      {open ? (
        <div
          role="dialog"
          aria-label="Choisir une période"
          className="absolute left-0 top-full z-10 mt-1 flex w-72 flex-col gap-3 rounded-md border border-border bg-card p-3 shadow-md"
        >
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="echanges-period-from">Du</Label>
            <Input
              id="echanges-period-from"
              type="date"
              value={from}
              max={to.length > 0 ? to : undefined}
              onChange={(event) => onChange(event.target.value, to)}
              className="h-9"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="echanges-period-to">Au</Label>
            <Input
              id="echanges-period-to"
              type="date"
              value={to}
              min={from.length > 0 ? from : undefined}
              onChange={(event) => onChange(from, event.target.value)}
              className="h-9"
            />
          </div>
          <div className="flex items-center justify-between">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={!hasRange}
              onClick={() => onChange('', '')}
            >
              Effacer
            </Button>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => setOpen(false)}
            >
              Fermer
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Liste des conversations unifiées (panneau gauche) — points 9, 10, 11.
// -----------------------------------------------------------------------------

interface EchangesConversationListProps {
  readonly conversations: readonly UnifiedConversation[];
  readonly selectedThreadId: string | null;
  readonly onSelectThread: (threadId: string) => void;
  readonly isLoading: boolean;
  readonly archivedHidden: boolean;
}

function EchangesConversationList({
  conversations,
  selectedThreadId,
  onSelectThread,
  isLoading,
  archivedHidden,
}: EchangesConversationListProps): React.JSX.Element {
  if (isLoading) {
    return (
      <p role="status" className="p-4 text-sm text-muted-foreground">
        Chargement de vos conversations…
      </p>
    );
  }

  // Mention persistante (point 11) : le vide sous la liste signale que des archivées sont masquées.
  const archivedNote = archivedHidden ? (
    <p className="px-4 py-3 text-xs italic text-muted-foreground">
      Les conversations archivées sont masquées.
    </p>
  ) : null;

  if (conversations.length === 0) {
    return (
      <div>
        <p className="p-4 text-sm text-muted-foreground">
          Aucune conversation à afficher.
        </p>
        {archivedNote}
      </div>
    );
  }

  return (
    <div>
      <ul className="flex flex-col divide-y divide-border">
        {conversations.map((conversation) => {
          const isSelected = conversation.threadId === selectedThreadId;
          const title =
            conversation.campaignName ?? `Campagne ${conversation.campaignId}`;
          return (
            <li key={conversation.threadId}>
              <button
                type="button"
                aria-current={isSelected ? true : undefined}
                onClick={() => onSelectThread(conversation.threadId)}
                className={cn(
                  'flex w-full flex-col gap-1 border-l-2 px-4 py-3 text-left text-sm',
                  'hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring',
                  isSelected
                    ? 'border-l-primary bg-accent text-accent-foreground'
                    : 'border-l-transparent',
                )}
              >
                <span className="flex items-baseline justify-between gap-2">
                  <span className="min-w-0 truncate font-medium">{title}</span>
                  <span className="shrink-0 font-mono text-[11px] text-muted-foreground">
                    {conversation.date !== null
                      ? formatShortDay(conversation.date)
                      : '—'}
                  </span>
                </span>
                <span className="flex items-center justify-between gap-2">
                  <span className="min-w-0 truncate text-xs text-muted-foreground">
                    {conversation.preview ?? 'Aucun message pour l’instant'}
                  </span>
                  <span className="flex shrink-0 items-center gap-1">
                    {conversation.isArchived ? (
                      <Badge variant="outline">Archivée</Badge>
                    ) : null}
                    {conversation.unread ? (
                      <Badge variant="invited" withDot>
                        Non lue
                      </Badge>
                    ) : null}
                  </span>
                </span>
              </button>
            </li>
          );
        })}
      </ul>
      {archivedNote}
    </div>
  );
}
